/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { ContactRolesSelect } from "~/components/Contacts/ContactRolesSelect";
import { TextAreaWrapper } from "~/components/Contacts/wrappers";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { RouteParams } from "~/models";
import { Contact } from "~/models/contact";
import { ACTIONS, Can } from "~/routing";
import { generateActionSuccessText, isValidEmail, useAxios } from "~/tools";
import { isValidPhone } from "~/tools/validation/checkPhone";

type AddContactProps = {
  refetch: () => void;
};

function AddContact({ refetch }: AddContactProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const methods = useForm();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const watcher = methods.watch();

  const state = useParams<RouteParams>();
  const { customerId } = state;
  const onSubmit = async (values: Contact) => {
    try {
      await Axios().post(`/customers/${customerId}/contacts`, values);
      refetch();
      showSuccess(generateActionSuccessText(`Contact`)()("added")());
      setTimeout(() => {
        setIsSidebarOpen(false);
      }, 200);
    } catch (e) {
      showError(e.response);
    }
  };

  return (
    <>
      <Can action={ACTIONS.ADD_CUSTOMER_CONTACT}>
        <Button icon="plus" onClick={() => setIsSidebarOpen(true)}>
          Add Contact
        </Button>
      </Can>

      <SidebarForm
        title="Add Contact"
        width="36rem"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField name="name" label="Name" required />
        <ContactRolesSelect />
        <SidebarTextField name="position" label="Position" required />
        <SidebarTextField
          name="email"
          label="Email"
          required
          registerOptions={{ validate: isValidEmail }}
        />
        <SidebarTextField
          name="phoneNumber"
          label="Phone number"
          registerOptions={{ validate: isValidPhone }}
        />
        <TextAreaWrapper>
          <SidebarTextField textArea name="comment" label="Comment" />
        </TextAreaWrapper>
        <TextAreaWrapper>
          <SidebarTextField
            textArea
            name="rulesOfEngagement"
            label="Rules of engagement"
          />
        </TextAreaWrapper>

        <SidebarFormButtons
          submitEnabled={!!(watcher.name && watcher.position && watcher.email)}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { AddContact };
